import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { useUsersManagementState } from '../context/users-management-context';
import { useUsersManagementController } from '../context/users-management-controller';
import MaskedTextField from '../../components/masked-text-field';
import PasswordField from '../../components/password-field';
import EntityAccessList from './entity-access-list';

const UsersManagementForm: React.FC = () => {
    const { userForm, isEditing, accessGroups, accessProfiles, personEntities } = useUsersManagementState();
    const {
        handleFormChange,
        handleAddEntityAccess,
        handleRemoveEntityAccess,
        handleUpdateEntityAccess
    } = useUsersManagementController();

    // Estado local para controlar o bloqueio do campo de grupo de acesso
    const [isAccessGroupLocked, setIsAccessGroupLocked] = useState(true);
    const [showWarningDialog, setShowWarningDialog] = useState(false);

    // Reseta o bloqueio quando o formulário é aberto (tanto para criação quanto edição)
    useEffect(() => {
        setIsAccessGroupLocked(true);
    }, [isEditing, userForm?.accessGroupId]);

    const handleFieldChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFormChange(field, event.target.value);
    };

    const handleEditAccessGroup = () => {
        setShowWarningDialog(true);
    };

    const handleConfirmUnlockAccessGroup = () => {
        setIsAccessGroupLocked(false);
        setShowWarningDialog(false);
    };

    const handleCancelUnlockAccessGroup = () => {
        setShowWarningDialog(false);
    };

    const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {icon}
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {title}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ px: 1 }}>
            <Grid container spacing={3}>
                {/* Informações Básicas */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<PersonIcon color="primary" />} title="Informações Básicas" />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome Completo"
                                    value={userForm?.name || ''}
                                    onChange={handleFieldChange('name')}
                                    required
                                    placeholder="Digite o nome completo do usuário"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={userForm?.email || ''}
                                    onChange={handleFieldChange('email')}
                                    required
                                    placeholder="usuario@exemplo.com"
                                    disabled={isEditing}
                                    InputProps={{
                                        readOnly: isEditing
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MaskedTextField
                                    maskType="cpf"
                                    fullWidth
                                    label="CPF"
                                    value={userForm?.documentNumber || ''}
                                    onChange={handleFieldChange('documentNumber')}
                                    required
                                    placeholder="000.000.000-00"
                                    disabled={isEditing}
                                    helperText={isEditing ? "CPF não pode ser alterado" : ""}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Segurança - Apenas na criação */}
                {!isEditing && (
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                            <SectionTitle icon={<LockIcon color="primary" />} title="Segurança" />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <PasswordField
                                        fullWidth
                                        label="Senha"
                                        value={userForm?.password || ''}
                                        onChange={(value) => handleFormChange('password', value)}
                                        required={true}
                                        showStrengthIndicator={true}
                                        showRequirements={true}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <PasswordField
                                        fullWidth
                                        label="Confirmar Senha"
                                        value={userForm?.confirmPassword || ''}
                                        onChange={(value) => handleFormChange('confirmPassword', value)}
                                        required={true}
                                        showStrengthIndicator={false}
                                        showRequirements={false}
                                        error={(userForm?.confirmPassword?.length ?? 0) > 0 && userForm?.password !== userForm?.confirmPassword}
                                        helperText={
                                            (userForm?.confirmPassword?.length ?? 0) > 0 && userForm?.password !== userForm?.confirmPassword
                                                ? "As senhas não coincidem"
                                                : ""
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                )}

                {/* Permissões */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <SectionTitle icon={<SecurityIcon color="primary" />} title="Permissões Gerais" />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <FormControl fullWidth required sx={{ flex: 1 }}>
                                        <InputLabel>Grupo de Acesso</InputLabel>
                                        <Select
                                            value={userForm?.accessGroupId || ''}
                                            onChange={(e) => handleFormChange('accessGroupId', e.target.value)}
                                            label="Grupo de Acesso"
                                            disabled={isAccessGroupLocked}
                                        >
                                            <MenuItem value="">
                                                <em>Selecione um grupo</em>
                                            </MenuItem>
                                            {accessGroups.map((group) => (
                                                <MenuItem key={group.accessGroupId} value={group.accessGroupId}>
                                                    {group.description}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {isAccessGroupLocked && (
                                        <Tooltip title="Editar grupo de acesso">
                                            <IconButton
                                                onClick={handleEditAccessGroup}
                                                color="primary"
                                                sx={{ mt: 1 }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Entidades de Acesso - Apenas para grupo Básico (id 3) */}
                {userForm?.accessGroupId === 3 && (
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                            <SectionTitle icon={<BusinessIcon color="primary" />} title="Vínculos com Entidades" />
                            <EntityAccessList
                                entityAccesses={userForm?.entityAccesses || []}
                                personEntities={personEntities}
                                accessProfiles={accessProfiles}
                                onAdd={handleAddEntityAccess}
                                onRemove={handleRemoveEntityAccess}
                                onChange={handleUpdateEntityAccess}
                            />
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Diálogo de aviso ao alterar grupo de acesso */}
            <Dialog
                open={showWarningDialog}
                onClose={handleCancelUnlockAccessGroup}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography variant="h6">Atenção: Alteração de Grupo de Acesso</Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Alterar o grupo de acesso pode conceder <strong>acesso total</strong> ao sistema caso você selecione um grupo diferente do padrão <strong>"Clientes"</strong>.
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2 }}>
                        Grupos como <strong>"Administrador"</strong> ou <strong>"Sistema"</strong> possuem permissões elevadas e acesso irrestrito a todas as funcionalidades.
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2, color: 'warning.main', fontWeight: 600 }}>
                        Tem certeza que deseja prosseguir?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCancelUnlockAccessGroup} variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmUnlockAccessGroup}
                        variant="contained"
                        color="warning"
                        startIcon={<EditIcon />}
                    >
                        Sim, Permitir Edição
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UsersManagementForm;
